import React, { useCallback, useMemo } from 'react';
import StoreBanner from './StoreBanner';
import { MuiTab, MuiMenu, MuiMenuPrint, MuiSwitch, MuiButton, MuiDialogue } from '@ace/ace-ui-web';
import Performance from '../../pages/performance/Performance';
import Operations from '../../pages/operations/Operations';
import styles from './Header.module.css';
import { Typography, Box, Grid } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useAppSelector, useAppDispatch } from '../../redux/hooks';
import {
  selectUserDetails,
  selectIsMultiStoreEnabled,
  selectIsSingleStoreUser,
  setIsMultiStoreEnabled,
} from '../../redux/UserReducer/userSlice';
import ScrollToTopButton from '../ScrollToTopButton';
import lang from '../../languages/en.json';
import { useBuildDynamicUrl } from '../../hooks/useDynamicUrl';
import { useClickHandler } from '../../utils/usageMetricsLogger';
import { useOperationsKpiData } from '../../hooks/useOperationsKpiData';
import { useOverallPerformanceGraphData } from '../../hooks/useOverallPerformanceGraphData';
import { useEcomOrderDetailsData } from '../../hooks/useEcomOrderDetailsData';
import { useKeyOperationalMetrics } from '../../hooks/useKeyOperationalMetrics';
import { useLocalLiftMaxData } from '../../hooks/useLocalLiftMaxData';
import { useSalesByFulfillmentData } from '../../hooks/useSalesByFulfillmentData';
import { useProductSyncData } from '../../hooks/useProductSyncData';
import { useYardRxData } from '../../hooks/useYardRxData';
import { selectIsAnyDataLoading } from '../../redux/HeaderReducer/printReadinessSelectors';
import {
  exportSummary,
  selectIsSummaryExportLoading,
} from '../../redux/HeaderReducer/summaryExportSlice';
import {
  downloadScorecard,
  selectIsScorecardExportLoading,
} from '../../redux/HeaderReducer/scorecardExportSlice';
import { getAdwDashboardsToken } from '../../redux/HeaderReducer/adwDashboardsTokenSlice';
import { getEffectiveStoreNumber } from '../../utils/appUtils';
import { RADRequest } from '../../models/common/RADRequest';
import { env } from '../../env';

// Module-level constants
const SCROLL_BUTTON_THRESHOLD = 300;
const ADW_REPORTS_BASE_URL_TYPE = 'ACENET';
const ADW_DASHBOARDS_BASE_URL_TYPE = 'RETAILER'; // Use ACENET for ADW dashboards and reports
const ADW_PILOT_PHASE = env.ADW_DASHBOARD_PILOT_MODE ?? false; // Default to false if not defined
const showADWDashboard = !ADW_PILOT_PHASE || new URLSearchParams(window.location.search).get('showADWDashboard') === 'true';    //showing ADW dashboard in pilot phase when you pass a URL param

interface TabItem {
  label: string;
  content: React.ReactNode;
}

const Header: React.FC = () => {
  const [isSummaryDialogOpen, setIsSummaryDialogOpen] = React.useState<boolean>(false);
  const [isScorecardDialogOpen, setIsScorecardDialogOpen] = React.useState<boolean>(false);
  const [scorecardDialogTitle, setScorecardDialogTitle] = React.useState<string>('');
  const [isADWDashboardDialogOpen, setIsADWDashboardDialogOpen] = React.useState<boolean>(false);
  const [activeTab, setActiveTab] = React.useState<number>(0);
  const renderPanels = false; // Change to false if you want to manually control content rendering

  // Scroll-to-top functionality - show button after scrolling down 300px
  const [showScrollButton, setShowScrollButton] = React.useState(false);

  const userDetails = useAppSelector(selectUserDetails);
  const isMultiStoreEnabled = useAppSelector(selectIsMultiStoreEnabled);
  const isSingleStoreUser = useAppSelector(selectIsSingleStoreUser);
  const isDataLoading = useAppSelector(selectIsAnyDataLoading);
  const isSummaryLoading = useAppSelector(selectIsSummaryExportLoading);
  const isScorecardLoading = useAppSelector(selectIsScorecardExportLoading);
  const dispatch = useAppDispatch();

  // Trigger API calls at header level - runs in parallel on page load/store change
  useOverallPerformanceGraphData();
  useOperationsKpiData();
  useEcomOrderDetailsData();
  useKeyOperationalMetrics();
  useLocalLiftMaxData();
  useSalesByFulfillmentData();
  useProductSyncData();
  useYardRxData();

  const isTablet = useMediaQuery('(max-width:1090px)');
  const isMobile = useMediaQuery('(max-width:995px)');
  const isSmallMobile = useMediaQuery('(max-width:600px)');

  React.useEffect(() => {
    const handleScroll = (): void => {
      const currentScrollY: number = window.scrollY;

      // Show scroll-to-top button when scrolled down more than 300px
      setShowScrollButton(currentScrollY > SCROLL_BUTTON_THRESHOLD);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return (): void => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const tabs: TabItem[] = useMemo(
    () => [
      {
        label: lang.header.performance,
        content: <Performance />,
      },
      {
        label: lang.header.operations,
        content: <Operations />,
      },
    ],
    [],
  );

  const menuItems: string[] = useMemo(
    () =>
      isSingleStoreUser
        ? [lang.header.peerGroup]
        : [lang.header.multiStore, lang.header.peerGroup],
    [isSingleStoreUser],
  );

  const buildDynamicUrl = useBuildDynamicUrl();
  const handleAndLogClick = useClickHandler('Header');

  const handleADWReportRedirect = useCallback((): void => {
    const url = buildDynamicUrl(ADW_REPORTS_BASE_URL_TYPE, 'ADW_REPORTS');

    if (!url) {
      // eslint-disable-next-line no-console
      console.warn('ADW redirect URL is not configured or failed to build.');
      return;
    }

    handleAndLogClick('ADW Reports', {
      url,
    });
  }, [buildDynamicUrl, handleAndLogClick]);

  const handleADWDashboardRedirect = useCallback((reportId?: string): void => {
    const isUrlEncoded = (str: string): boolean => {
      try {
        // If decoding changes the string, it was encoded.
        return str !== decodeURIComponent(str);
      } catch (error) {
        // If decodeURIComponent throws an error (e.g., "malformed URI sequence" due to a lone %),
        // the string contains unencoded percent symbols, meaning it is not properly encoded.
        // eslint-disable-next-line no-console
        console.warn('Failed to decode URI component in isUrlEncoded check.', error);
        return false;
      }
    };

    const buildAdwDashboardUrl = (
      baseUrl: string,
      adwToken: string | undefined,
      reportId: string | undefined,
      ibToken: string | undefined,
    ): string => {
      if (!adwToken) {
        return baseUrl;
      }
      let allQueries = '';
      if (reportId) {
        allQueries += '&reportId=' + (!isUrlEncoded(reportId) ? encodeURIComponent(reportId) : reportId);
      }
      if (ibToken) {
        // ibToken is already urlEncoded, so no need to encode it again
        allQueries += '&IBToken=' + ibToken;
      }
      allQueries += '&ADWToken=' + (!isUrlEncoded(adwToken) ? encodeURIComponent(adwToken) : adwToken);
      // url already has portalCode query, no need to add '?'
      return baseUrl + allQueries;
    };

    // The ADW Dashboards URL is resolved server-side (session cookie based) via
    // RAD API's GetAdwDashboardsUrl. Because the API call is async, we must open
    // the tab synchronously here — otherwise the browser treats the later
    // window.open as an unsolicited popup and blocks it.
    const placeholderTab = window.open('about:blank', '_blank');
    void (async (): Promise<void> => {
      try {
        handleAndLogClick(lang.header.adwDashboards);
        let url = buildDynamicUrl(ADW_DASHBOARDS_BASE_URL_TYPE, 'ADW_DASHBOARDS');

        const storeNumber = getEffectiveStoreNumber(userDetails, false);    // Returns current store in single-store mode

        const requestBody: RADRequest = {
          storeNumber: storeNumber,
          isMultiStoreMode: false
        };

        const response = await dispatch(getAdwDashboardsToken(requestBody)).unwrap();

        if (!url || !response?.token || !userDetails?.ibToken) {
          if (placeholderTab && !placeholderTab.closed) {
            placeholderTab.close();
          }
          setIsADWDashboardDialogOpen(true);
          return;
        }

        url = buildAdwDashboardUrl(url, response?.token, reportId, userDetails?.ibToken);

        if (placeholderTab && !placeholderTab.closed) {
          placeholderTab.location.href = url;
        } else {
          // Popup was blocked — fall back to same-tab navigation.
          window.location.href = url;
          setIsADWDashboardDialogOpen(true);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('Failed to resolve ADW Dashboards URL.', error);
        if (placeholderTab && !placeholderTab.closed) {
          placeholderTab.close();
        }
        setIsADWDashboardDialogOpen(true);
      }
    })();
  }, [ buildDynamicUrl, handleAndLogClick, dispatch, userDetails ]);

  const handleCloseADWDashboardDialog = useCallback((): void => {
    setIsADWDashboardDialogOpen(false);
  }, []);

  const handleSummaryClick = useCallback(async (): Promise<void> => {
    const chainStores = userDetails?.userChainList?.map(store => store.storeNumber) ?? [];
    // Chain export is inherently a multi-store operation, so resolve the parent store
    // regardless of the current UI toggle.
    const parentStoreNumber = getEffectiveStoreNumber(userDetails, true);

    if (!parentStoreNumber || chainStores.length === 0) {
      setIsSummaryDialogOpen(true);
      return;
    }

    const requestBody: RADRequest = {
      storeNumber: parentStoreNumber,
      isMultiStoreMode: true,
      storeNumbers: chainStores,
    };

    try {
      await dispatch(exportSummary(requestBody)).unwrap();
    } catch {
      setIsSummaryDialogOpen(true);
    }
  }, [userDetails, dispatch]);

  const handleCloseDialog = useCallback((): void => {
    setIsSummaryDialogOpen(false);
  }, []);

  const handlePrintSelected = useCallback(
    (selectedItems: string[]): void => {
      // The pointerEvents: 'none' wrapper only blocks mouse input — keyboard
      // activation (Tab + Enter) bypasses it, so guard the handler as well.
      if (isDataLoading) {
        return;
      }
      handleAndLogClick(`Print - ${selectedItems.join(', ')}`);
      const urls: string[] = [];
      if (selectedItems.includes('Operations')) urls.push('/print-operations');
      if (selectedItems.includes('Performance')) urls.push('/print-performance');
      // Open each URL with a staggered delay — browsers block multiple window.open
      // calls fired synchronously in the same event, treating them as popup spam.
      urls.forEach((url, i) => {
        setTimeout(() => window.open(url, '_blank'), i * 300);
      });
    },
    [handleAndLogClick, isDataLoading],
  );

  const handleScorecardMenuItemClick = useCallback(
    async (itemName: string): Promise<void> => {
      setScorecardDialogTitle(itemName);

      // Multi-Store reports use the parent/effective store; Peer Group reports
      // use the user's currently selected store. Pass `isMultiStoreMode` to
      // `getEffectiveStoreNumber` so its behavior matches the mode sent to the API.
      const isMultiStoreMode = itemName === lang.header.multiStore;
      const chainStores = userDetails?.userChainList?.map(store => store.storeNumber) ?? [];
      const effectiveStore = userDetails
        ? getEffectiveStoreNumber(userDetails, isMultiStoreMode)
        : '';

      // Peer Group scorecards are valid for single-store users (empty chain list),
      // so only require a populated chain when running in Multi-Store mode.
      if (!effectiveStore || (isMultiStoreMode && chainStores.length === 0)) {
        setIsScorecardDialogOpen(true);
        return;
      }

      const requestBody: RADRequest = {
        storeNumber: effectiveStore,
        isMultiStoreMode,
        storeNumbers: chainStores,
      };

      try {
        await dispatch(downloadScorecard(requestBody)).unwrap();
      } catch {
        setIsScorecardDialogOpen(true);
      }
    },
    [userDetails, dispatch],
  );

  const handleCloseScorecardDialog = useCallback((): void => {
    setIsScorecardDialogOpen(false);
  }, []);

  const handleMultiStoreToggle = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, checked: boolean): void => {
      if (checked) {
        handleAndLogClick('Multi-store Enabled');
      }
      dispatch(setIsMultiStoreEnabled(checked));
    },
    [dispatch, handleAndLogClick],
  );

  const handlePerformanceClick = useCallback((): void => {
    setActiveTab(0);
    handleAndLogClick(lang.header.performance);
  }, [handleAndLogClick]);

  const handleOperationsClick = useCallback((): void => {
    setActiveTab(1);
    handleAndLogClick(lang.header.operations);
  }, [handleAndLogClick]);

  const handleTabChange = useCallback(
    (tabIndex: number): void => {
      setActiveTab(tabIndex);
      handleAndLogClick(tabs[tabIndex]?.label);
    },
    [handleAndLogClick, tabs],
  );

  const scrollToTop = (): void => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          backgroundColor: 'white',
          minWidth: 'fit-content',
        }}>
        <StoreBanner
          onPerformanceClick={handlePerformanceClick}
          onOperationsClick={handleOperationsClick}
          onADWReportClick={handleADWReportRedirect}
          onADWDashboardClick={handleADWDashboardRedirect}
          onMultiStoreToggle={handleMultiStoreToggle}
          onPrintClick={handlePrintSelected}
          isPrintDisabled={isDataLoading}
          onSummaryClick={() => void handleSummaryClick()}
        />
      </Box>
      <Box
        component="nav"
        sx={{
          margin: '0px',
          padding: '3px 24px',
          backgroundColor: 'var(--color-background-paper)',
          overflowX: 'auto',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
        }}>
        <Grid
          container
          spacing={0}
          alignItems="center"
          sx={{
            flexWrap: 'nowrap',
            minWidth: isMobile ? 'max-content' : 'auto',
          }}>
          <Grid size="auto">
            <Box className={styles.muiTabContainer}>
              <MuiTab
                fontSize={isSmallMobile ? 'small' : 'default'}
                fontWeight="bold"
                tabs={tabs}
                variant="button"
                renderPanels={renderPanels}
                value={activeTab}
                onChange={handleTabChange}
              />
            </Box>
          </Grid>
          <Grid size="auto">
            <Box
              className={styles.scorecardMenu}
              sx={{
                ...(isScorecardLoading && {
                  pointerEvents: 'none',
                  opacity: 0.5,
                  cursor: 'not-allowed',
                }),
              }}>
              <MuiMenu
                buttonText={lang.header.scorecard}
                colors="tertiaryText"
                showDropdownIcon={true}
                variant="text"
                menuItems={menuItems}
                size={isSmallMobile ? 'small' : 'medium'}
                onMenuItemClick={item => {
                  // The pointerEvents: 'none' wrapper only blocks mouse input — keyboard
                  // activation (Tab + Enter) bypasses it, so guard the handler as well.
                  if (isScorecardLoading) {
                    return;
                  }
                  handleAndLogClick(`Scorecard - ${item}`, {
                    callback: () => handleScorecardMenuItemClick(item),
                  });
                }}
                {...(isScorecardLoading && { startIcon: 'restartAlt' })}
              />
            </Box>
          </Grid>
          {showADWDashboard && <Grid size="auto">
            <Box
              className={styles.scorecardMenu}
            >
              <MuiMenu
                buttonText={lang.header.reporting}
                colors="tertiaryText"
                showDropdownIcon={true}
                variant="text"
                menuItems={[lang.header.adwReports, lang.header.adwDashboards]}
                size={isSmallMobile ? 'small' : 'medium'}
                onMenuItemClick={item => {
                  // Each redirect handler already calls handleAndLogClick with the
                  // resolved URL, so avoid wrapping here to prevent a duplicate
                  // ClickEvent being logged for every reporting menu selection.
                  if (item === lang.header.adwReports) {
                    handleADWReportRedirect();
                  } else if (item === lang.header.adwDashboards) {
                    handleADWDashboardRedirect();
                  }
                }}
              />
            </Box>
          </Grid>}
          <Grid
            size="grow"
            sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Box className={styles.rightButtons}>
              {!showADWDashboard && <MuiButton
                colors="tertiary"
                variant="outlined"
                size={isTablet ? 'small' : 'medium'}
                iconPosition="left"
                iconType="launch"
                onClick={handleADWReportRedirect}>
                {lang.header.adwReports}
              </MuiButton>}
              {!isMobile && (
                <MuiButton
                  colors="tertiary"
                  variant="outlined"
                  size={isTablet ? 'small' : 'medium'}
                  iconPosition="left"
                  iconType={isSummaryLoading ? 'restartAlt' : 'downloadOutlined'}
                  onClick={() => handleAndLogClick('Summary Download', { callback: () => void handleSummaryClick() })}
                  disabled={isSummaryLoading}>
                  {lang.header.summary}
                </MuiButton>
              )}
              <Box
                sx={{
                  ...(isDataLoading && {
                    pointerEvents: 'none',
                    opacity: 0.5,
                    cursor: 'not-allowed',
                  }),
                }}>
                <MuiMenuPrint
                  size={isTablet ? 'small' : 'medium'}
                  onPrintSelected={handlePrintSelected}
                  {...(isDataLoading && { startIcon: 'restartAlt' })}
                />
              </Box>
              {userDetails?.userChainList && userDetails?.userChainList?.length > 1 && (
                <Box className={styles.muiSwitchContainer}>
                  <MuiSwitch
                    key={String(isMultiStoreEnabled)}
                    label={lang.pageContent.multiStoreView}
                    defaultChecked={isMultiStoreEnabled}
                    onChange={handleMultiStoreToggle}
                    size={isTablet ? 'small' : 'medium'}
                  />
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Manually render tab content only when renderPanels is false */}
      {!renderPanels && <Box sx={{ padding: '0 24px' }}>{tabs[activeTab].content}</Box>}

      <MuiDialogue
        open={isADWDashboardDialogOpen}
        hideTriggerButton
        dialogTitle={lang.header.adwDashboards}
        onClose={handleCloseADWDashboardDialog}
        dialogContent={
          <Box>
            <Typography
              variant="body1"
              gutterBottom>
              {lang.pageContent.adwDashboardTokenGenerationError}
            </Typography>
          </Box>
        }
      />
      <MuiDialogue
        open={isScorecardDialogOpen}
        hideTriggerButton
        dialogTitle={scorecardDialogTitle}
        onClose={handleCloseScorecardDialog}
        dialogContent={
          <Box>
            <Typography
              variant="body1"
              gutterBottom>
              {lang.pageContent.dataUnavailableError.replace('{section}', scorecardDialogTitle)}
            </Typography>
          </Box>
        }
      />
      <MuiDialogue
        open={isSummaryDialogOpen}
        hideTriggerButton
        dialogTitle={lang.storeBanner.summaryDownload}
        onClose={handleCloseDialog}
        dialogContent={
          <Box>
            <Typography
              variant="body1"
              gutterBottom>
              {lang.pageContent.dataUnavailableError.replace(
                '{section}',
                lang.storeBanner.summary,
              )}
            </Typography>
          </Box>
        }
      />
      {/* Global Scroll-to-Top Button */}
      <ScrollToTopButton
        isShow={showScrollButton}
        onClick={scrollToTop}
      />
    </>
  );
};

export default Header;
